import React from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  IconButton,
  Link as MuiLink,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { useCookieConsent } from './CookieConsentContext';
import { ALL_PREFERENCES } from '../../shared/utils/consentStorage';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';

const contentUseCase = new ContentUseCase();

export const CookieBanner: React.FC = () => {
  const { language } = useLanguage();
  const lp = useLocalizedPath();
  const { setCookieConsent, showBanner } = useCookieConsent();
  const { cookieBanner } = contentUseCase.getLegalContent(language);

  const handleAccept = () => {
    // Accept all cookies with all preferences enabled
    setCookieConsent('accepted', ALL_PREFERENCES);
  };

  const handleDecline = () => {
    setCookieConsent('declined');
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: 'background.paper',
        borderTop: '2px solid',
        borderColor: 'primary.main',
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ py: 3, position: 'relative' }}>
          <IconButton
            onClick={handleDecline}
            sx={{
              position: 'absolute',
              right: 0,
              top: 8,
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" gutterBottom>
            {cookieBanner.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
            {cookieBanner.description}
            {' '}
            <MuiLink component={Link} to={lp('/cookies')} underline="hover">
              {cookieBanner.cookiePolicy}
            </MuiLink>
            {' | '}
            <MuiLink component={Link} to={lp('/privacy')} underline="hover">
              {cookieBanner.privacyPolicy}
            </MuiLink>
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAccept}
              size="small"
            >
              {cookieBanner.acceptAll}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to={lp('/cookie-settings')}
              size="small"
            >
              {cookieBanner.settings}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDecline}
              size="small"
            >
              {cookieBanner.declineAll}
            </Button>
          </Box>
        </Box>
      </Container>
    </Paper>
  );
};

